export const FLIGHT_DETAILS_UNKNOWN = 'Flight details unknown';

export interface BookingFlightDetails {
  airport?: string | null;
  flight_number?: string | null;
  departure_date?: string | null;
  departure_time?: string | null;
  pnr?: string | null;
  status?: string | null;
  status_raw?: string | null;
  status_checked_at?: string | null;
  departure_delay_minutes?: number | null;
  scheduled_departure?: string | null;
  estimated_departure?: string | null;
  actual_departure?: string | null;
  scheduled_arrival?: string | null;
  estimated_arrival?: string | null;
  actual_arrival?: string | null;
  arrival_airport?: string | null;
  departure_gate?: string | null;
  departure_terminal?: string | null;
}

export function flightHasDetails(flight?: BookingFlightDetails | null): boolean {
  if (!flight) return false;
  return Boolean(
    (flight.airport || '').trim()
    || (flight.flight_number || '').trim()
    || (flight.departure_date || '').trim()
    || (flight.departure_time || '').trim()
    || (flight.pnr || '').trim()
  );
}

function formatDepartureDate(isoDate?: string | null): string {
  if (!isoDate) return '';
  const d = new Date(`${isoDate.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatFlightStatusDisplay(
  flight?: BookingFlightDetails | null,
  statusText?: string | null
): string | null {
  if (statusText && statusText.trim() && statusText !== 'Status unknown') {
    return statusText.trim();
  }
  if (!flightHasDetails(flight)) return null;
  const status = (flight?.status || '').trim();
  if (!status) return 'Scheduled';
  const delay = flight?.departure_delay_minutes;
  if (status === 'Delayed' && typeof delay === 'number' && delay > 0) {
    return `Delayed (${delay} min)`;
  }
  return status;
}

export function flightStatusChipColor(status?: string | null): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' {
  const text = (status || '').toLowerCase();
  if (text.startsWith('delayed') || text === 'incident') return 'warning';
  if (text === 'in flight') return 'primary';
  if (text === 'landed') return 'success';
  if (text === 'cancelled' || text === 'diverted') return 'error';
  if (text === 'scheduled') return 'info';
  return 'default';
}

export function formatFlightDisplay(
  flight?: BookingFlightDetails | null,
  displayText?: string | null
): string {
  if (displayText && displayText.trim()) return displayText.trim();
  if (!flightHasDetails(flight)) return FLIGHT_DETAILS_UNKNOWN;

  const airport = (flight?.airport || '').trim().toUpperCase();
  const flightNumber = (flight?.flight_number || '').trim();
  const departureTime = (flight?.departure_time || '').trim();
  const departureDate = formatDepartureDate(flight?.departure_date);
  const pnr = (flight?.pnr || '').trim();

  const parts: string[] = [];
  if (airport) parts.push(`from ${airport} airport`);
  if (flightNumber) parts.push(`on flight ${flightNumber}`);
  if (departureTime && departureDate) {
    parts.push(`departing at ${departureTime} on ${departureDate}`);
  } else if (departureDate) {
    parts.push(`departing on ${departureDate}`);
  } else if (departureTime) {
    parts.push(`departing at ${departureTime}`);
  }

  let sentence = parts.join(' ').trim();
  if (pnr) sentence = `${sentence} (PNR: ${pnr})`.trim();
  return sentence || FLIGHT_DETAILS_UNKNOWN;
}

export function buildFlightPayload(fields: {
  airport: string;
  flight_number: string;
  departure_date: string;
  departure_time: string;
  pnr: string;
}): BookingFlightDetails | null {
  const payload: BookingFlightDetails = {
    airport: fields.airport.trim().toUpperCase() || null,
    flight_number: fields.flight_number.trim() || null,
    departure_date: fields.departure_date.trim() || null,
    departure_time: fields.departure_time.trim() || null,
    pnr: fields.pnr.trim() || null,
  };
  return flightHasDetails(payload) ? payload : null;
}

export function flightToFormFields(flight?: BookingFlightDetails | null) {
  return {
    airport: flight?.airport || '',
    flight_number: flight?.flight_number || '',
    departure_date: flight?.departure_date?.slice(0, 10) || '',
    departure_time: flight?.departure_time || '',
    pnr: flight?.pnr || '',
  };
}
